import React from 'react';
import { NotificationBell } from '../NotificationComponents';
import { NotificationPopup, PushNotificationOptIn } from '../NotificationComponents';
import TrialStatusBanner from '../TrialStatusBanner';
import FuelNearbyCard from '../FuelNearbyCard';
import RecentCompares from '../RecentCompares';
import SmartRecommendations from '../SmartRecommendations';
import SavingsForecastWidget from '../SavingsForecastWidget';
import BottomNavigation from '../BottomNavigation';
import BetaWelcomeModal from '../BetaWelcomeModal';
import FounderWelcomeModal from '../FounderWelcomeModal';
import ProWelcomeModal from '../ProWelcomeModal';
import FreeWelcomeModal from '../FreeWelcomeModal';

// Contributor Level Badges
const LEVEL_INFO = {
  1: { name: 'Novice', color: 'bg-gray-500', icon: '🌱', multiplier: '1.0x' },
  2: { name: 'Contributor', color: 'bg-blue-500', icon: '⭐', multiplier: '1.2x' },
  3: { name: 'Expert', color: 'bg-purple-500', icon: '💎', multiplier: '1.5x' },
  4: { name: 'Guru', color: 'bg-yellow-500', icon: '👑', multiplier: '2.0x' }
};

export default function DashboardView({
  userId, userData, subscriptionInfo, isAlphaTester,
  handleLogout, handleNavigation,
  setActiveTab, setActiveView, activeTab, handleTabChange,
  showBetaModal, setShowBetaModal,
  showFounderModal, setShowFounderModal, founderModalTier,
  showProModal, setShowProModal, isProFamily,
  showFreeModal, setShowFreeModal,
}) {
  // Alpha Tester takes priority over contributor level
  const isQualifiedFreeForLife = isAlphaTester && 
    userData?.lifetime_access && 
    (userData?.approved_submissions || 0) >= 30;
  
  const levelInfo = isAlphaTester 
    ? { 
        name: isQualifiedFreeForLife ? 'Qualified – Free for Life' : 'Alpha Tester', 
        color: 'bg-gradient-to-r from-purple-500 to-pink-500', 
        icon: '👑', 
        multiplier: 'LIFETIME FREE' 
      }
    : LEVEL_INFO[userData?.contributor_level || 1];
  
  const tierInfo = subscriptionInfo || {};

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex justify-between items-center">
          <h1 className="text-lg font-bold text-green-600" data-testid="app-header">PricePAL</h1>
          <div className="flex items-center gap-2">
            <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium" data-testid="user-tier-badge">
              {isAlphaTester ? 'Alpha' : 
               userData?.subscription_tier === 'VIP_FOUNDER' ? 'VIP' :
               userData?.subscription_tier === 'EARLY_FOUNDER' ? 'Founder' :
               userData?.trial_active ? 'Premium Trial' :
               userData?.subscription_tier === 'INDIVIDUAL' ? 'Premium' :
               userData?.subscription_tier === 'FAMILY' ? 'Family' : 'Free'}
            </span>
            <NotificationBell />
            <button onClick={handleLogout} className="text-xs text-gray-400 hover:text-gray-600 font-medium" data-testid="logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* TRIAL STATUS BANNER */}
      {userData?.trial_active && (
        <TrialStatusBanner userData={userData} onUpgrade={() => setActiveView('upgrade-view')} />
      )}

      <main className="max-w-7xl mx-auto px-4 pt-5 pb-4">
        {/* HERO SEARCH */}
        <div className="mb-4">
          <div 
            onClick={() => {
              setActiveTab('compare');
              setActiveView('compare-parent');
              window.scrollTo(0, 0);
            }}
            className="bg-white border-2 border-green-500 rounded-2xl px-4 py-4 flex items-center gap-3 cursor-pointer hover:border-green-600 hover:shadow-md transition-all shadow-sm"
            data-testid="search-bar-cta"
          >
            <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="text-gray-400 text-sm">Search milk, iPhone, shampoo...</span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1.5 text-center" data-testid="search-microcopy">Search products manually</p>
        </div>

        {/* PREDICTED SAVINGS FORECAST — Self-Learning teaser */}
        <SavingsForecastWidget
          userId={userId}
          userData={userData}
          onNavigate={(view) => {
            // Translate widget actions to the dashboard's navigation contract
            if (view === 'compare') {
              setActiveTab('compare');
              setActiveView('compare-parent');
            } else if (view === 'receipts') {
              setActiveView('receipt-scanner');
            } else {
              setActiveView('savings');
            }
            window.scrollTo(0, 0);
          }}
        />

        {/* Primary CTA */}
        <button
          onClick={() => {
            setActiveTab('compare');
            setActiveView('compare-parent');
            window.scrollTo(0, 0);
          }}
          className="w-full bg-green-600 hover:bg-green-700 text-white rounded-2xl py-4 px-6 flex flex-col items-center justify-center gap-0.5 shadow-lg hover:shadow-xl transition-all mb-3 active:scale-[0.98]"
          data-testid="primary-cta-compare"
        >
          <span className="text-lg font-bold tracking-tight">Compare Prices</span>
          <span className="text-[11px] text-green-200 font-medium">Scan or enter barcode to find cheapest store</span>
        </button>

        {/* Secondary CTA */}
        <button
          onClick={() => {
            setActiveTab('scan');
            setActiveView('scan-parent');
            window.scrollTo(0, 0);
          }}
          className="w-full bg-white border border-gray-200 text-gray-600 rounded-2xl py-3 px-6 flex flex-col items-center justify-center gap-0.5 hover:bg-gray-50 hover:border-gray-300 transition-all mb-5 active:scale-[0.98]"
          data-testid="secondary-cta-scan"
        >
          <span className="font-semibold text-sm text-gray-700">Scan & Submit</span>
          <span className="text-[10px] text-gray-400">Personalise your app · Help others save</span>
        </button>

        {/* Fuel */}
        <div className="mb-5">
          <FuelNearbyCard userId={userId} onViewAll={() => { setActiveView('fuel-prices'); window.scrollTo(0, 0); }} />
        </div>

        {/* Recent Compares */}
        <RecentCompares onCompare={(barcode, name) => {
          setActiveTab('compare');
          setActiveView('compare-prices');
          setTimeout(() => {
            window.__pricepal_prefill_barcode = barcode;
            window.__pricepal_prefill_name = name;
          }, 100);
        }} />

        {/* Personalised Recommendations */}
        <div className="mb-6">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2" data-testid="ai-section-heading">
            Recommended for you
          </p>
          <SmartRecommendations
            userId={userId}
            onCompare={(barcode, name) => {
              setActiveTab('compare');
              setActiveView('compare-prices');
              setTimeout(() => {
                window.__pricepal_prefill_barcode = barcode;
                window.__pricepal_prefill_name = name;
              }, 100);
            }}
          />
        </div>
      </main>

      {/* Modals */}
      {showBetaModal && (
        <BetaWelcomeModal 
          onClose={() => setShowBetaModal(false)} 
          userName={userData?.name || 'Explorer'} 
        />
      )}
      {showFounderModal && (
        <FounderWelcomeModal
          onClose={() => setShowFounderModal(false)}
          userName={userData?.name || 'Founder'}
          tier={founderModalTier}
        />
      )}
      {showProModal && (
        <ProWelcomeModal
          onClose={() => setShowProModal(false)}
          userName={userData?.name || 'Pro Member'}
          isFamily={isProFamily}
        />
      )}
      {showFreeModal && (
        <FreeWelcomeModal
          onClose={() => setShowFreeModal(false)}
          userName={userData?.name || 'there'}
          onUpgrade={() => handleNavigation('upgrade')}
        />
      )}
      
      {/* BOTTOM NAVIGATION */}
      <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />

      {/* NOTIFICATION POPUP (shown on login for unread important notifications) */}
      <NotificationPopup />

      {/* PUSH NOTIFICATION OPT-IN (shown 10s after load, if not already opted in) */}
      <PushNotificationOptIn />
    </div>
  );
}
